// ═══════════════════════════════════════════════════════════════════════════════
// FOLLOW-UP DASHBOARD v2.0 — Priority-Based Follow-Up System
// EnergiPro Solutions — Automated Follow-Up Admin Panel with RFQ Scoring
// ═══════════════════════════════════════════════════════════════════════════════
// Features: RFQ Score, Priority Badges, Priority Alerts, Manual Override,
//           Priority-based schedules (High: Day 1/2/4, Med: 1/3/7/14, Low: 3/7/14)
// ═══════════════════════════════════════════════════════════════════════════════

import React, { useState, useEffect } from 'react';
import { Layout } from '../../components/layout/Layout';
import {
  Mail, Clock, CheckCircle2, AlertTriangle, Pause, Play, Send,
  XCircle, Calendar, RefreshCw, Eye, ChevronDown, ChevronUp,
  MessageSquare, Activity, Zap, Shield, ArrowRight, Target,
  AlertCircle, TrendingUp, ArrowUpCircle
} from 'lucide-react';
import {
  FollowUpTrackingRecord, FollowUpStage, FollowUpStatus, RFQPriorityLevel,
  generateMockFollowUpData, processFollowUpAction, markClientReplied,
  formatDaysAgo, formatNextFollowUp, getStageProgress, getNextStageInfo,
  FOLLOW_UP_SCHEDULE, HIGH_PRIORITY_FOLLOW_UP, LOW_PRIORITY_FOLLOW_UP,
  SENDER_EMAIL, getEffectivePriority, getScheduleForPriority, getMaxStagesForPriority,
  overridePriority
} from '../../components/rfq/followUpEngine';
import {
  generateFollowUpEmail, renderFollowUpAsText, renderFollowUpAsHTML
} from '../../components/rfq/followUpTemplates';

// ─── STATUS BADGE STYLES ─────────────────────────────────────────────────────

const STATUS_STYLES: Record<FollowUpStatus, { bg: string; text: string; border: string; label: string }> = {
  active: { bg: 'bg-green-500/10', text: 'text-green-300', border: 'border-green-500/30', label: 'Active' },
  paused: { bg: 'bg-amber-500/10', text: 'text-amber-300', border: 'border-amber-500/30', label: 'Paused' },
  completed: { bg: 'bg-slate-500/10', text: 'text-slate-400', border: 'border-slate-500/30', label: 'Completed' },
  stopped: { bg: 'bg-red-500/10', text: 'text-red-300', border: 'border-red-500/30', label: 'Stopped' },
};

const RESPONSE_STYLES: Record<string, { bg: string; text: string; label: string }> = {
  awaiting: { bg: 'bg-blue-500/10', text: 'text-blue-300', label: 'Awaiting Response' },
  replied: { bg: 'bg-green-500/10', text: 'text-green-300', label: 'Client Replied' },
  'no-response': { bg: 'bg-red-500/10', text: 'text-red-300', label: 'No Response' },
  declined: { bg: 'bg-slate-500/10', text: 'text-slate-400', label: 'Declined' },
};

const PRIORITY_BADGE: Record<RFQPriorityLevel, { bg: string; text: string; border: string; label: string; ring: string }> = {
  high: { bg: 'bg-red-500/15', text: 'text-red-300', border: 'border-red-500/40', label: 'HIGH', ring: 'ring-red-500/30' },
  medium: { bg: 'bg-amber-500/15', text: 'text-amber-300', border: 'border-amber-500/40', label: 'MEDIUM', ring: 'ring-amber-500/30' },
  low: { bg: 'bg-green-500/15', text: 'text-green-300', border: 'border-green-500/40', label: 'LOW', ring: 'ring-green-500/30' },
};

// ─── MAIN COMPONENT ──────────────────────────────────────────────────────────

export const FollowUpDashboard: React.FC = () => {
  const [records, setRecords] = useState<FollowUpTrackingRecord[]>([]);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [previewId, setPreviewId] = useState<string | null>(null);
  const [previewStage, setPreviewStage] = useState<1 | 2 | 3 | 4>(1);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [priorityFilter, setPriorityFilter] = useState<string>('all');
  const [notification, setNotification] = useState<string | null>(null);
  const [overrideModalId, setOverrideModalId] = useState<string | null>(null);
  const [dismissedAlerts, setDismissedAlerts] = useState<Set<string>>(new Set());

  useEffect(() => {
    setRecords(generateMockFollowUpData());
  }, []);

  // ─── ACTIONS ─────────────────────────────────────────────────────────────

  const handlePause = (rfqId: string) => {
    setRecords(prev => prev.map(r => {
      if (r.rfqId !== rfqId) return r;
      const { updatedRecord } = processFollowUpAction(r, { type: 'pause', rfqId, reason: 'Admin paused' }, 'Admin');
      return updatedRecord;
    }));
    showNotification('Follow-up automation paused');
  };

  const handleResume = (rfqId: string) => {
    setRecords(prev => prev.map(r => {
      if (r.rfqId !== rfqId) return r;
      const { updatedRecord } = processFollowUpAction(r, { type: 'resume', rfqId }, 'Admin');
      return updatedRecord;
    }));
    showNotification('Follow-up automation resumed');
  };

  const handleManualSend = (rfqId: string) => {
    setRecords(prev => prev.map(r => {
      if (r.rfqId !== rfqId) return r;
      const { updatedRecord } = processFollowUpAction(r, { type: 'manual_send', rfqId }, 'Admin');
      return updatedRecord;
    }));
    showNotification('Manual follow-up sent successfully');
  };

  const handleMarkReplied = (rfqId: string) => {
    setRecords(prev => prev.map(r => {
      if (r.rfqId !== rfqId) return r;
      return markClientReplied(r);
    }));
    showNotification('Client marked as replied — automation stopped');
  };

  const handleOverridePriority = (rfqId: string, newPriority: RFQPriorityLevel) => {
    setRecords(prev => prev.map(r => {
      if (r.rfqId !== rfqId) return r;
      return overridePriority(r, newPriority, 'Admin');
    }));
    setOverrideModalId(null);
    showNotification(`Priority overridden to ${newPriority.toUpperCase()}`);
  };

  const showNotification = (msg: string) => {
    setNotification(msg);
    setTimeout(() => setNotification(null), 4000);
  };

  // ─── FILTERS ─────────────────────────────────────────────────────────────

  const filteredRecords = records.filter(r => {
    if (statusFilter !== 'all' && r.automationStatus !== statusFilter) return false;
    if (priorityFilter !== 'all' && getEffectivePriority(r) !== priorityFilter) return false;
    return true;
  });

  // Sort: high priority first, then by score descending
  const sortedRecords = [...filteredRecords].sort((a, b) => {
    const prioOrder = { high: 0, medium: 1, low: 2 };
    const aPrio = prioOrder[getEffectivePriority(a)];
    const bPrio = prioOrder[getEffectivePriority(b)];
    if (aPrio !== bPrio) return aPrio - bPrio;
    return (b.rfqScore || 0) - (a.rfqScore || 0);
  });

  // ─── KPIs ────────────────────────────────────────────────────────────────

  const activeCount = records.filter(r => r.automationStatus === 'active').length;
  const awaitingCount = records.filter(r => r.clientResponseStatus === 'awaiting').length;
  const highPriorityCount = records.filter(r => getEffectivePriority(r) === 'high' && r.automationStatus === 'active').length;
  const repliedCount = records.filter(r => r.clientResponseStatus === 'replied').length;

  // High-priority active alerts (not dismissed)
  const highPriorityAlerts = records.filter(r =>
    getEffectivePriority(r) === 'high' &&
    r.automationStatus === 'active' &&
    !dismissedAlerts.has(r.rfqId)
  );

  return (
    <Layout>
      <div className="min-h-screen bg-[#0a0f1e] py-12">
        <div className="max-w-7xl mx-auto px-4">
          {/* ═══ HEADER ═══ */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="font-['Oswald'] text-3xl text-white uppercase tracking-wide">Follow-Up Automation</h1>
              <p className="text-slate-400 text-sm mt-1">
                Priority-based follow-up engine — RFQ scoring & intelligent scheduling
              </p>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2 px-3 py-2 bg-green-500/10 border border-green-500/30 rounded-lg">
                <Activity className="w-4 h-4 text-green-400 animate-pulse" />
                <span className="text-green-300 text-sm font-medium">Engine Active</span>
              </div>
              <div className="flex items-center gap-2 px-3 py-2 bg-orange-500/10 border border-orange-500/30 rounded-lg">
                <Shield className="w-4 h-4 text-orange-400" />
                <span className="text-orange-300 text-sm font-medium">Admin</span>
              </div>
            </div>
          </div>

          {/* Notification */}
          {notification && (
            <div className="mb-4 p-3 rounded-lg border bg-green-500/10 border-green-500/30 flex items-center gap-2 animate-pulse">
              <CheckCircle2 className="w-4 h-4 text-green-400" />
              <span className="text-green-300 text-sm">{notification}</span>
            </div>
          )}

          {/* ═══ HIGH PRIORITY ALERTS ═══ */}
          {highPriorityAlerts.length > 0 && (
            <div className="mb-6 p-5 bg-red-500/5 border border-red-500/30 rounded-xl animate-pulse">
              <div className="flex items-center gap-3 mb-3">
                <AlertCircle className="w-5 h-5 text-red-400" />
                <h3 className="text-red-300 font-bold text-sm uppercase">
                  ⚠️ High Priority Alert — Immediate Action Required ({highPriorityAlerts.length})
                </h3>
              </div>
              <div className="space-y-2">
                {highPriorityAlerts.map(r => (
                  <div key={r.rfqId} className="flex items-center justify-between p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
                    <div className="flex items-center gap-3">
                      <span className="w-8 h-8 rounded-full bg-red-500/20 border border-red-500/40 flex items-center justify-center">
                        <TrendingUp className="w-4 h-4 text-red-400" />
                      </span>
                      <div>
                        <p className="text-white text-sm font-medium">{r.clientName} — {r.companyName}</p>
                        <p className="text-red-300/70 text-xs">{r.serviceName} | Score: {r.rfqScore}/100</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-red-300 text-xs font-bold">RESPOND IMMEDIATELY</span>
                      <button
                        onClick={() => setDismissedAlerts(prev => new Set([...prev, r.rfqId]))}
                        className="px-2 py-1 bg-slate-800 border border-slate-700 rounded text-slate-400 text-xs hover:text-white transition-all"
                      >
                        Dismiss
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ═══ KPI STRIP ═══ */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <KPICard icon={<Zap className="w-4 h-4 text-green-400" />} label="Active Sequences" value={activeCount} color="green" />
            <KPICard icon={<Target className="w-4 h-4 text-red-400" />} label="High Priority" value={highPriorityCount} color="red" />
            <KPICard icon={<Clock className="w-4 h-4 text-blue-400" />} label="Awaiting Reply" value={awaitingCount} color="blue" />
            <KPICard icon={<MessageSquare className="w-4 h-4 text-emerald-400" />} label="Client Replied" value={repliedCount} color="emerald" />
          </div>

          {/* ═══ PRIORITY-BASED SCHEDULE REFERENCE ═══ */}
          <div className="mb-6 bg-[#111827] border border-slate-700/50 rounded-xl p-5">
            <h3 className="text-white font-bold text-sm mb-3 flex items-center gap-2">
              <Calendar className="w-4 h-4 text-orange-400" /> Priority-Based Follow-Up Schedules
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* High Priority */}
              <div className="bg-red-500/5 border border-red-500/20 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <span className="px-2 py-0.5 bg-red-500/20 border border-red-500/40 rounded text-red-300 text-[10px] font-bold">HIGH (80+)</span>
                </div>
                <div className="space-y-1.5">
                  {HIGH_PRIORITY_FOLLOW_UP.map(s => (
                    <div key={s.stage} className="flex items-center gap-2 text-xs">
                      <span className="w-5 h-5 rounded-full bg-red-500/20 border border-red-500/30 flex items-center justify-center text-red-300 text-[10px] font-bold">{s.stage}</span>
                      <span className="text-slate-300">Day {s.dayOffset}</span>
                      <span className="text-slate-500">— {s.label}</span>
                    </div>
                  ))}
                </div>
              </div>
              {/* Medium Priority */}
              <div className="bg-amber-500/5 border border-amber-500/20 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <span className="px-2 py-0.5 bg-amber-500/20 border border-amber-500/40 rounded text-amber-300 text-[10px] font-bold">MEDIUM (50–79)</span>
                </div>
                <div className="space-y-1.5">
                  {FOLLOW_UP_SCHEDULE.map(s => (
                    <div key={s.stage} className="flex items-center gap-2 text-xs">
                      <span className="w-5 h-5 rounded-full bg-amber-500/20 border border-amber-500/30 flex items-center justify-center text-amber-300 text-[10px] font-bold">{s.stage}</span>
                      <span className="text-slate-300">Day {s.dayOffset}</span>
                      <span className="text-slate-500">— {s.label}</span>
                    </div>
                  ))}
                </div>
              </div>
              {/* Low Priority */}
              <div className="bg-green-500/5 border border-green-500/20 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <span className="px-2 py-0.5 bg-green-500/20 border border-green-500/40 rounded text-green-300 text-[10px] font-bold">LOW (&lt;50)</span>
                </div>
                <div className="space-y-1.5">
                  {LOW_PRIORITY_FOLLOW_UP.map(s => (
                    <div key={s.stage} className="flex items-center gap-2 text-xs">
                      <span className="w-5 h-5 rounded-full bg-green-500/20 border border-green-500/30 flex items-center justify-center text-green-300 text-[10px] font-bold">{s.stage}</span>
                      <span className="text-slate-300">Day {s.dayOffset}</span>
                      <span className="text-slate-500">— {s.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* ═══ FILTER PILLS ═══ */}
          <div className="flex items-center gap-3 mb-2 flex-wrap">
            <span className="text-slate-500 text-xs uppercase">Status:</span>
            {['all', 'active', 'paused', 'completed', 'stopped'].map(f => (
              <button
                key={f}
                onClick={() => setStatusFilter(f)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all capitalize ${
                  statusFilter === f
                    ? 'bg-orange-500/10 border-orange-500/40 text-orange-300'
                    : 'bg-slate-800 border-slate-700 text-slate-400 hover:text-slate-300'
                }`}
              >
                {f} ({f === 'all' ? records.length : records.filter(r => r.automationStatus === f).length})
              </button>
            ))}
          </div>
          <div className="flex items-center gap-3 mb-4 flex-wrap">
            <span className="text-slate-500 text-xs uppercase">Priority:</span>
            {['all', 'high', 'medium', 'low'].map(f => (
              <button
                key={f}
                onClick={() => setPriorityFilter(f)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all capitalize ${
                  priorityFilter === f
                    ? 'bg-orange-500/10 border-orange-500/40 text-orange-300'
                    : 'bg-slate-800 border-slate-700 text-slate-400 hover:text-slate-300'
                }`}
              >
                {f} ({f === 'all' ? records.length : records.filter(r => getEffectivePriority(r) === f).length})
              </button>
            ))}
          </div>

          {/* ═══ RECORDS LIST ═══ */}
          <div className="space-y-4">
            {sortedRecords.map(record => (
              <FollowUpCard
                key={record.rfqId}
                record={record}
                isExpanded={expandedId === record.rfqId}
                onToggleExpand={() => setExpandedId(expandedId === record.rfqId ? null : record.rfqId)}
                onPause={() => handlePause(record.rfqId)}
                onResume={() => handleResume(record.rfqId)}
                onManualSend={() => handleManualSend(record.rfqId)}
                onMarkReplied={() => handleMarkReplied(record.rfqId)}
                onPreview={(stage) => { setPreviewId(record.rfqId); setPreviewStage(stage); }}
                onOverridePriority={() => setOverrideModalId(record.rfqId)}
              />
            ))}
          </div>

          {sortedRecords.length === 0 && (
            <div className="text-center py-16 bg-[#111827] border border-slate-700/50 rounded-xl">
              <Mail className="w-12 h-12 text-slate-600 mx-auto mb-3" />
              <p className="text-slate-400 text-lg">No follow-up records match this filter</p>
            </div>
          )}

          {/* ═══ EMAIL PREVIEW MODAL ═══ */}
          {previewId && (
            <EmailPreviewModal
              record={records.find(r => r.rfqId === previewId)!}
              stage={previewStage}
              onClose={() => setPreviewId(null)}
            />
          )}

          {/* ═══ PRIORITY OVERRIDE MODAL ═══ */}
          {overrideModalId && (
            <PriorityOverrideModal
              record={records.find(r => r.rfqId === overrideModalId)!}
              onClose={() => setOverrideModalId(null)}
              onOverride={(priority) => handleOverridePriority(overrideModalId, priority)}
            />
          )}
        </div>
      </div>
    </Layout>
  );
};

// ─── KPI CARD ────────────────────────────────────────────────────────────────

const KPICard: React.FC<{ icon: React.ReactNode; label: string; value: number; color: string }> = ({ icon, label, value, color }) => (
  <div className={`bg-[#111827] border border-${color}-500/20 rounded-xl p-5`}>
    <div className="flex items-center gap-2 mb-2">
      <div className={`p-1.5 bg-${color}-500/10 rounded-lg`}>{icon}</div>
      <span className="text-slate-400 text-xs uppercase">{label}</span>
    </div>
    <p className="text-3xl font-bold text-white font-['Oswald']">{value}</p>
  </div>
);

// ─── FOLLOW-UP CARD (with Priority) ─────────────────────────────────────────

interface FollowUpCardProps {
  record: FollowUpTrackingRecord;
  isExpanded: boolean;
  onToggleExpand: () => void;
  onPause: () => void;
  onResume: () => void;
  onManualSend: () => void;
  onMarkReplied: () => void;
  onPreview: (stage: 1 | 2 | 3 | 4) => void;
  onOverridePriority: () => void;
}

const FollowUpCard: React.FC<FollowUpCardProps> = ({
  record, isExpanded, onToggleExpand, onPause, onResume, onManualSend, onMarkReplied, onPreview, onOverridePriority
}) => {
  const statusStyle = STATUS_STYLES[record.automationStatus];
  const responseStyle = RESPONSE_STYLES[record.clientResponseStatus];
  const effectivePriority = getEffectivePriority(record);
  const priorityStyle = PRIORITY_BADGE[effectivePriority];
  const schedule = getScheduleForPriority(effectivePriority);
  const maxStages = getMaxStagesForPriority(effectivePriority);
  const nextInfo = getNextStageInfo(record);
  const progress = getStageProgress(record.followUpStage, effectivePriority);
  const isHighPriority = effectivePriority === 'high';

  return (
    <div className={`bg-[#111827] border rounded-xl overflow-hidden transition-all ${
      isHighPriority ? 'border-red-500/40 ring-1 ring-red-500/20' : 'border-slate-700/50'
    }`}>
      {/* High Priority Indicator Bar */}
      {isHighPriority && (
        <div className="h-1 bg-gradient-to-r from-red-500 via-orange-500 to-red-500" />
      )}

      {/* Main Row */}
      <div className="p-5">
        <div className="flex items-start justify-between gap-4">
          {/* Left: Client Info + Priority */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <span className="text-orange-300 font-mono text-xs font-bold">{record.referenceId}</span>
              {/* Priority Badge */}
              <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase border ${priorityStyle.bg} ${priorityStyle.text} ${priorityStyle.border}`}>
                {priorityStyle.label}
              </span>
              {/* RFQ Score */}
              {record.rfqScore !== undefined && (
                <span className={`px-2 py-0.5 rounded text-[10px] font-mono font-bold ${
                  record.rfqScore >= 80 ? 'bg-red-500/10 text-red-300' :
                  record.rfqScore >= 50 ? 'bg-amber-500/10 text-amber-300' :
                  'bg-green-500/10 text-green-300'
                }`}>
                  Score: {record.rfqScore}
                </span>
              )}
              {/* Override indicator */}
              {record.priorityOverride && (
                <span className="px-2 py-0.5 rounded text-[10px] font-medium bg-purple-500/10 text-purple-300 border border-purple-500/30">
                  Override by {record.priorityOverrideBy}
                </span>
              )}
              <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase border ${statusStyle.bg} ${statusStyle.text} ${statusStyle.border}`}>
                {statusStyle.label}
              </span>
              <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${responseStyle.bg} ${responseStyle.text}`}>
                {responseStyle.label}
              </span>
            </div>
            <p className="text-white font-semibold text-sm">{record.clientName} — {record.companyName}</p>
            <p className="text-slate-400 text-xs mt-1 truncate">{record.serviceName}</p>
            <p className="text-slate-500 text-[10px] mt-1">
              <span className="text-slate-600">Email:</span> {record.clientEmail}
            </p>
          </div>

          {/* Center: Stage Progress */}
          <div className="flex-shrink-0 w-48">
            <div className="flex items-center justify-between mb-1">
              <span className="text-slate-500 text-[10px] uppercase">Stage</span>
              <span className="text-white text-xs font-bold">{record.followUpStage}/{maxStages}</span>
            </div>
            {/* Progress Bar */}
            <div className="h-2 bg-slate-700 rounded-full overflow-hidden mb-2">
              <div
                className={`h-full rounded-full transition-all duration-500 ${
                  record.automationStatus === 'stopped' ? 'bg-red-500' :
                  record.automationStatus === 'completed' ? 'bg-slate-500' :
                  isHighPriority ? 'bg-red-500' : 'bg-orange-500'
                }`}
                style={{ width: `${progress}%` }}
              />
            </div>
            {/* Next Email */}
            <div className="flex items-center gap-1.5">
              <Clock className="w-3 h-3 text-slate-500" />
              <span className="text-slate-400 text-[10px]">
                Next: <span className="text-white font-medium">{formatNextFollowUp(record)}</span>
              </span>
            </div>
            {nextInfo && record.automationStatus === 'active' && (
              <p className="text-slate-600 text-[10px] mt-0.5">
                #{nextInfo.stage} {nextInfo.label}
              </p>
            )}
          </div>

          {/* Right: Actions */}
          <div className="flex-shrink-0 flex items-center gap-2 flex-wrap">
            {record.automationStatus === 'active' && (
              <>
                <button
                  onClick={onPause}
                  className="flex items-center gap-1 px-3 py-1.5 bg-amber-500/10 border border-amber-500/30 rounded-lg text-amber-300 text-xs hover:bg-amber-500/20 transition-all"
                  title="Pause follow-up"
                >
                  <Pause className="w-3 h-3" /> Pause
                </button>
                <button
                  onClick={onManualSend}
                  className="flex items-center gap-1 px-3 py-1.5 bg-blue-500/10 border border-blue-500/30 rounded-lg text-blue-300 text-xs hover:bg-blue-500/20 transition-all"
                  title="Send manual follow-up"
                >
                  <Send className="w-3 h-3" /> Manual
                </button>
              </>
            )}
            {record.automationStatus === 'paused' && (
              <button
                onClick={onResume}
                className="flex items-center gap-1 px-3 py-1.5 bg-green-500/10 border border-green-500/30 rounded-lg text-green-300 text-xs hover:bg-green-500/20 transition-all"
                title="Resume follow-up"
              >
                <Play className="w-3 h-3" /> Resume
              </button>
            )}
            {record.clientResponseStatus === 'awaiting' && (
              <button
                onClick={onMarkReplied}
                className="flex items-center gap-1 px-3 py-1.5 bg-emerald-500/10 border border-emerald-500/30 rounded-lg text-emerald-300 text-xs hover:bg-emerald-500/20 transition-all"
                title="Mark client as replied"
              >
                <MessageSquare className="w-3 h-3" /> Replied
              </button>
            )}
            {/* Priority Override Button */}
            <button
              onClick={onOverridePriority}
              className="flex items-center gap-1 px-3 py-1.5 bg-purple-500/10 border border-purple-500/30 rounded-lg text-purple-300 text-xs hover:bg-purple-500/20 transition-all"
              title="Override priority"
            >
              <ArrowUpCircle className="w-3 h-3" /> Priority
            </button>
            <button
              onClick={onToggleExpand}
              className="flex items-center gap-1 px-3 py-1.5 bg-slate-700 border border-slate-600 rounded-lg text-slate-300 text-xs hover:bg-slate-600 transition-all"
            >
              {isExpanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
              {isExpanded ? 'Close' : 'Logs'}
            </button>
          </div>
        </div>

        {/* Metadata Row */}
        <div className="mt-3 flex items-center gap-4 text-[10px] text-slate-500 flex-wrap">
          <span>Proposal Sent: <span className="text-slate-300">{formatDaysAgo(record.proposalSentDate)}</span></span>
          {record.lastEmailSentDate && (
            <span>Last Email: <span className="text-slate-300">{formatDaysAgo(record.lastEmailSentDate)}</span></span>
          )}
          <span>Schedule: <span className={`font-medium ${
            effectivePriority === 'high' ? 'text-red-300' :
            effectivePriority === 'low' ? 'text-green-300' : 'text-amber-300'
          }`}>{effectivePriority === 'high' ? 'Day 1/2/4' : effectivePriority === 'low' ? 'Day 3/7/14' : 'Day 1/3/7/14'}</span></span>
          {record.pausedBy && <span className="text-amber-500">Paused by: {record.pausedBy}</span>}
          {record.stoppedReason && <span className="text-red-400">Stopped: {record.stoppedReason}</span>}
        </div>
      </div>

      {/* Expanded: Log History + Email Previews */}
      {isExpanded && (
        <div className="border-t border-slate-700/50 bg-slate-900/30 p-5">
          {/* Stage Email Previews */}
          <div className="mb-4">
            <h4 className="text-white text-xs font-bold mb-2 flex items-center gap-2">
              <Eye className="w-3.5 h-3.5 text-orange-400" /> Preview Email Templates ({effectivePriority.toUpperCase()} schedule)
            </h4>
            <div className="flex gap-2 flex-wrap">
              {schedule.map((s, idx) => (
                <button
                  key={idx}
                  onClick={() => onPreview((idx + 1) as 1 | 2 | 3 | 4)}
                  className={`px-3 py-1.5 rounded-lg text-xs border transition-all ${
                    record.followUpStage >= idx + 1
                      ? 'bg-green-500/10 border-green-500/30 text-green-300'
                      : 'bg-slate-800 border-slate-700 text-slate-400 hover:text-slate-300'
                  }`}
                >
                  #{s.stage} {s.label} (Day {s.dayOffset})
                </button>
              ))}
            </div>
          </div>

          {/* Activity Log */}
          <h4 className="text-white text-xs font-bold mb-2 flex items-center gap-2">
            <Activity className="w-3.5 h-3.5 text-blue-400" /> Activity Log ({record.history.length} entries)
          </h4>
          <div className="space-y-2 max-h-60 overflow-y-auto">
            {record.history.slice().reverse().map(log => (
              <div key={log.id} className="flex items-start gap-3 p-2.5 bg-slate-800/50 border border-slate-700/30 rounded-lg">
                <div className="flex-shrink-0 mt-0.5">
                  {log.action === 'email_sent' && <Send className="w-3.5 h-3.5 text-blue-400" />}
                  {log.action === 'email_failed' && <XCircle className="w-3.5 h-3.5 text-red-400" />}
                  {log.action === 'paused' && <Pause className="w-3.5 h-3.5 text-amber-400" />}
                  {log.action === 'resumed' && <Play className="w-3.5 h-3.5 text-green-400" />}
                  {log.action === 'stopped' && <XCircle className="w-3.5 h-3.5 text-red-400" />}
                  {log.action === 'client_replied' && <MessageSquare className="w-3.5 h-3.5 text-emerald-400" />}
                  {log.action === 'manual_send' && <Send className="w-3.5 h-3.5 text-purple-400" />}
                  {log.action === 'status_changed' && <RefreshCw className="w-3.5 h-3.5 text-slate-400" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-slate-200 text-xs">{log.description}</p>
                  <div className="flex items-center gap-3 mt-1">
                    <span className="text-slate-600 text-[10px]">{new Date(log.timestamp).toLocaleString()}</span>
                    <span className="text-slate-600 text-[10px]">Actor: {log.actor}</span>
                    {log.deliveryStatus && (
                      <span className={`text-[10px] ${log.deliveryStatus === 'delivered' ? 'text-green-500' : log.deliveryStatus === 'failed' ? 'text-red-500' : 'text-blue-500'}`}>
                        ● {log.deliveryStatus}
                      </span>
                    )}
                    {log.error && <span className="text-red-400 text-[10px]">{log.error}</span>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ─── PRIORITY OVERRIDE MODAL ─────────────────────────────────────────────────

const PriorityOverrideModal: React.FC<{
  record: FollowUpTrackingRecord;
  onClose: () => void;
  onOverride: (priority: RFQPriorityLevel) => void;
}> = ({ record, onClose, onOverride }) => {
  const currentPriority = getEffectivePriority(record);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" onClick={onClose}>
      <div className="bg-[#111827] border border-slate-700 rounded-2xl w-full max-w-md overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="p-5 border-b border-slate-700/50">
          <h3 className="text-white font-bold flex items-center gap-2">
            <ArrowUpCircle className="w-5 h-5 text-purple-400" />
            Manual Priority Override
          </h3>
          <p className="text-slate-500 text-xs mt-1">
            {record.referenceId} — {record.clientName}
          </p>
          <p className="text-slate-600 text-xs mt-1">
            Current Score: {record.rfqScore || '—'} | Current Priority: <span className="font-bold uppercase">{currentPriority}</span>
            {record.priorityOverride && <span className="text-purple-400 ml-2">(Overridden)</span>}
          </p>
        </div>
        <div className="p-5 space-y-3">
          <p className="text-slate-400 text-sm mb-4">Select new priority level (overrides calculated score):</p>
          {(['high', 'medium', 'low'] as const).map(p => {
            const style = PRIORITY_BADGE[p];
            const isActive = currentPriority === p;
            return (
              <button
                key={p}
                onClick={() => onOverride(p)}
                disabled={isActive}
                className={`w-full p-4 rounded-lg border text-left transition-all ${
                  isActive
                    ? `${style.bg} ${style.border} opacity-50 cursor-not-allowed`
                    : `bg-slate-800 border-slate-700 hover:${style.bg} hover:${style.border}`
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className={`px-2.5 py-1 rounded text-xs font-bold uppercase border ${style.bg} ${style.text} ${style.border}`}>
                      {style.label}
                    </span>
                    <span className="text-slate-300 text-sm">
                      {p === 'high' && 'Day 1 → Day 2 (Escalation) → Day 4 (Urgency)'}
                      {p === 'medium' && 'Day 1 → Day 3 → Day 7 → Day 14'}
                      {p === 'low' && 'Day 3 → Day 7 → Day 14'}
                    </span>
                  </div>
                  {isActive && <span className="text-slate-500 text-xs">Current</span>}
                </div>
              </button>
            );
          })}
        </div>
        <div className="p-4 border-t border-slate-700/50 flex items-center justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-slate-300 text-sm hover:bg-slate-700 transition-all"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── EMAIL PREVIEW MODAL ─────────────────────────────────────────────────────

const EmailPreviewModal: React.FC<{
  record: FollowUpTrackingRecord;
  stage: 1 | 2 | 3 | 4;
  onClose: () => void;
}> = ({ record, stage, onClose }) => {
  const [viewMode, setViewMode] = useState<'structured' | 'text' | 'html'>('structured');
  const email = generateFollowUpEmail(record, stage);
  const [copied, setCopied] = useState(false);
  const effectivePriority = getEffectivePriority(record);
  const schedule = getScheduleForPriority(effectivePriority);
  const stageInfo = schedule[stage - 1] || FOLLOW_UP_SCHEDULE[stage - 1];

  const handleCopy = () => {
    navigator.clipboard.writeText(renderFollowUpAsText(email));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" onClick={onClose}>
      <div className="bg-[#111827] border border-slate-700 rounded-2xl w-full max-w-3xl max-h-[85vh] overflow-hidden" onClick={e => e.stopPropagation()}>
        {/* Modal Header */}
        <div className="p-5 border-b border-slate-700/50 flex items-center justify-between">
          <div>
            <h3 className="text-white font-bold flex items-center gap-2">
              <Mail className="w-4 h-4 text-orange-400" />
              Follow-Up #{stage}: {stageInfo?.label || 'Unknown'}
            </h3>
            <p className="text-slate-500 text-xs mt-1">
              Day {stageInfo?.dayOffset || '?'} — {record.referenceId} → {record.clientEmail}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {(['structured', 'text', 'html'] as const).map(m => (
              <button
                key={m}
                onClick={() => setViewMode(m)}
                className={`px-3 py-1.5 rounded-lg text-xs border transition-all capitalize ${
                  viewMode === m
                    ? 'bg-orange-500/10 border-orange-500/40 text-orange-300'
                    : 'bg-slate-800 border-slate-700 text-slate-400'
                }`}
              >
                {m}
              </button>
            ))}
            <button onClick={onClose} className="ml-2 p-2 rounded-lg bg-slate-800 border border-slate-700 text-slate-400 hover:text-white transition-all">
              <XCircle className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Modal Body */}
        <div className="p-5 overflow-y-auto max-h-[65vh]">
          {viewMode === 'structured' && (
            <div className="space-y-4">
              <div className="p-3 bg-slate-800/50 rounded-lg border border-slate-700/30">
                <p className="text-slate-500 text-[10px] uppercase mb-1">Subject</p>
                <p className="text-white text-sm font-medium">{email.subject}</p>
              </div>
              <div className="p-3 bg-slate-800/50 rounded-lg border border-slate-700/30">
                <p className="text-slate-500 text-[10px] uppercase mb-1">From</p>
                <p className="text-slate-300 text-sm">{SENDER_EMAIL}</p>
              </div>
              <div className="p-3 bg-slate-800/50 rounded-lg border border-slate-700/30">
                <p className="text-slate-500 text-[10px] uppercase mb-1">To</p>
                <p className="text-slate-300 text-sm">{record.clientEmail}</p>
              </div>
              <div>
                <p className="text-white text-sm font-medium mb-2">{email.greeting}</p>
                {email.body.map((p, i) => (
                  <p key={i} className="text-slate-300 text-sm leading-relaxed mb-3">{p}</p>
                ))}
              </div>
              <div className="p-4 bg-orange-500/5 border-l-4 border-orange-500 rounded-r-lg">
                <p className="text-orange-200 text-sm font-medium">{email.callToAction}</p>
              </div>
              <p className="text-slate-400 text-sm">{email.closing}</p>
              <pre className="text-slate-500 text-xs whitespace-pre-wrap font-sans">{email.signature}</pre>
            </div>
          )}

          {viewMode === 'text' && (
            <pre className="text-slate-300 text-sm whitespace-pre-wrap font-sans leading-relaxed bg-slate-900 p-4 rounded-lg border border-slate-700/50">
              {renderFollowUpAsText(email)}
            </pre>
          )}

          {viewMode === 'html' && (
            <div
              className="bg-white rounded-lg overflow-hidden"
              dangerouslySetInnerHTML={{ __html: renderFollowUpAsHTML(email) }}
            />
          )}
        </div>

        {/* Modal Footer */}
        <div className="p-4 border-t border-slate-700/50 flex items-center justify-between">
          <p className="text-slate-600 text-xs">
            Template generated for {record.clientName} — {record.companyName}
          </p>
          <button
            onClick={handleCopy}
            className="flex items-center gap-2 px-4 py-2 bg-orange-500/10 border border-orange-500/40 rounded-lg text-orange-300 text-sm font-medium hover:bg-orange-500/20 transition-all"
          >
            {copied ? <CheckCircle2 className="w-4 h-4" /> : <ArrowRight className="w-4 h-4" />}
            {copied ? 'Copied!' : 'Copy to Clipboard'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default FollowUpDashboard;
